import { pileDePolice } from '../../lib/polices';
import { FRAIS_CAT } from '../../constants/accounting';
import { DEVISE, fmt } from '../../lib/currency';
import { printDoc } from '../../lib/print';
import { depensesApi } from '../../api/resources';
import { ouvreVisionneuse } from '../../components/Visionneuse.jsx';
import { tr } from '../../lib/i18n';
import { libellePaiement } from '../../lib/paiement-depense';
import { tauxDepense } from '../../lib/vat';
import { C } from '../../theme/palette';
import { PleinEcran } from '../../components/ui/PleinEcran.jsx';

const cellule = { padding: '9px 0' };
const etiquette = { ...cellule, color: C.grey, width: '38%' };
const valeur = { ...cellule, fontWeight: 700, textAlign: 'right' };

function Ligne({ l, children, premiere }) {
  return (
    <tr style={premiere ? undefined : { borderTop: '1px solid ' + C.border }}>
      <td style={etiquette}>{l}</td>
      <td style={valeur}>{children}</td>
    </tr>
  );
}

/**
 * Justificatif de dépense au format A4.
 *
 * Une dépense payée de sa poche — un plein, une boîte de vis, un parking — n'a
 * souvent pour preuve qu'un ticket froissé. Ce document lui donne la forme
 * d'une pièce comptable : l'entreprise en tête, le compte, le montant ventilé
 * hors taxe et TVA, et le ticket photographié en dessous. C'est ce que
 * réclame la fiduciaire, et ce que l'administration demandera si elle
 * contrôle.
 *
 * @param {object} props propriétés du composant
 * @param {object} props.dep dépense à présenter
 * @param {object} [props.soc] entreprise émettrice
 * @param {object[]} [props.chantiers] chantiers, pour retrouver l'imputation
 * @param {Function} props.onClose ferme la vue
 * @returns {import('react').ReactElement} la pièce, prête à imprimer
 */
export function DepenseA4View({ dep, soc, chantiers, onClose }) {
  const d = dep || {};
  const cat = FRAIS_CAT[d.type] || {};
  // Le taux de la famille, a la date du ticket : un repas de 2023 se
  // deduit a 2,5 %, le meme repas en 2024 a 2,6 %.
  const taux = tauxDepense(d, FRAIS_CAT, soc);
  const ttc = +d.montant || 0;
  // Le montant saisi est celui du ticket, TVA comprise : c'est ce qui a ete
  // paye. La part deductible s'en deduit, elle ne s'ajoute pas.
  const ht = ttc / (1 + taux / 100);
  const tva = ttc - ht;
  const chantier = (chantiers || []).find((c) => String(c.id) === String(d.chantierId));
  const piece = d.piece || d.justificatif || '';
  const estImage = (d.pieceMime || '').startsWith('image/');

  return (
    <PleinEcran>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          paddingTop: 'var(--st-haut)',
          zIndex: 9999,
          background: '#5b5f66',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            background: C.navy,
            padding: '10px 14px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            ‹ Retour
          </button>
          {d.originalId ? (
            <button
              onClick={() =>
                ouvreVisionneuse({ adresse: depensesApi.adresseOriginal(d.originalId) }) ||
                window.open(depensesApi.adresseOriginal(d.originalId), '_blank', 'noopener')
              }
              style={{
                marginLeft: 'auto',
                marginRight: 8,
                background: 'rgba(255,255,255,.18)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '8px 12px',
                fontSize: 12,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              {d.pieceMime === 'application/pdf' || /\.pdf$/i.test(d.pieceNom || '')
                ? tr('📄 Facture d’origine')
                : '📷 ' + tr('Photo d’origine')}
            </button>
          ) : null}
          <button
            onClick={printDoc}
            style={{
              background: C.green,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 14px',
              fontSize: 12,
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            🖨 Imprimer / PDF
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 10px' }}>
          <div
            className="a4-sheet"
            style={{
              maxWidth: 760,
              margin: '0 auto',
              background: '#fff',
              borderRadius: 6,
              padding: '30px 34px',
              boxShadow: '0 2px 12px rgba(0,0,0,.25)',
              fontSize: 13,
              color: C.ink,
              boxSizing: 'border-box',
              fontFamily: pileDePolice(soc?.docFont) || 'inherit',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                borderBottom: '2px solid ' + C.navy,
                paddingBottom: 14,
                marginBottom: 18,
              }}
            >
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                {soc?.logo ? (
                  <img
                    src={soc.logo}
                    alt=""
                    style={{ width: 52, height: 52, objectFit: 'contain' }}
                  />
                ) : null}
                <div>
                  <div style={{ fontSize: 17, fontWeight: 900, color: C.navy }}>
                    {soc?.nom || 'Mon entreprise'}
                  </div>
                  <div style={{ fontSize: 10, color: C.grey, lineHeight: 1.5 }}>
                    {soc?.adr || ''}
                    {soc?.npa ? ' · ' + soc.npa : ''}
                  </div>
                  {soc?.tva ? (
                    <div style={{ fontSize: 9.5, color: C.muted }}>N° TVA {soc.tva}</div>
                  ) : null}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 15, fontWeight: 900 }}>JUSTIFICATIF</div>
                <div style={{ fontSize: 15, fontWeight: 900, marginBottom: 4 }}>DE DÉPENSE</div>
                <div style={{ fontSize: 10, color: C.grey }}>N° {d.id}</div>
                <div style={{ fontSize: 10, color: C.grey }}>{d.date}</div>
              </div>
            </div>

            <table
              style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 18, fontSize: 12 }}
            >
              <tbody>
                <Ligne l="Catégorie" premiere>
                  {cat.icon || ''} {cat.lib || d.type || '—'}
                  {cat.compte ? ' (' + cat.compte + ')' : ''}
                </Ligne>
                <Ligne l="Libellé">{d.libelle || d.lib || '—'}</Ligne>
                {d.fournisseur ? <Ligne l="Fournisseur">{d.fournisseur}</Ligne> : null}
                {chantier ? <Ligne l="Chantier">{chantier.nom}</Ligne> : null}
                <Ligne l="Date">{d.date}</Ligne>
                {d.paiement ? (
                  <Ligne l="Moyen de paiement">{tr(libellePaiement(d.paiement))}</Ligne>
                ) : null}
              </tbody>
            </table>

            <div
              style={{
                background: C.navyXL,
                border: '1px solid ' + C.border,
                borderRadius: 8,
                padding: '14px 18px',
                marginBottom: 20,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11,
                  color: C.grey,
                  marginBottom: 6,
                }}
              >
                <span>Montant hors TVA</span>
                <span>
                  {DEVISE} {fmt(Math.round(ht * 100) / 100)}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11,
                  color: C.grey,
                  marginBottom: 6,
                }}
              >
                <span>TVA {taux}% (déductible)</span>
                <span>
                  {DEVISE} {fmt(Math.round(tva * 100) / 100)}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 16,
                  fontWeight: 900,
                  color: C.navy,
                  borderTop: '1px solid ' + C.border,
                  paddingTop: 8,
                }}
              >
                <span>Total TTC</span>
                <span>
                  {DEVISE} {fmt(ttc)}
                </span>
              </div>
            </div>

            {piece ? (
              estImage ? (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 10, color: C.grey, marginBottom: 6, fontWeight: 700 }}>
                    Justificatif
                  </div>
                  <img
                    src={piece}
                    alt="Justificatif"
                    style={{
                      maxWidth: '100%',
                      maxHeight: 520,
                      border: '1px solid ' + C.border,
                      borderRadius: 6,
                      display: 'block',
                    }}
                  />
                </div>
              ) : (
                <div style={{ marginTop: 10, fontSize: 11, color: C.grey }}>
                  📄 Justificatif joint : {d.pieceNom || 'pièce jointe'}
                </div>
              )
            ) : (
              <div style={{ marginTop: 10, fontSize: 10, color: C.muted, fontStyle: 'italic' }}>
                Aucun justificatif joint.
              </div>
            )}

            <div
              style={{
                marginTop: 28,
                paddingTop: 14,
                borderTop: '1px solid ' + C.border,
                fontSize: 9,
                color: C.muted,
                textAlign: 'center',
              }}
            >
              Document généré par {soc?.nom || 'SwissTech'} · Pièce comptable
            </div>
          </div>
        </div>
      </div>
    </PleinEcran>
  );
}
